import React from 'react'
import {
  DialogActions,
  DialogActionsProps,
  DialogContent,
  Divider,
  styled,
  SxProps,
  Theme,
  TypographyProps,
} from '@mui/material'
import { t } from 'i18next'
import { CancelButton } from './CancelButton'
import { CustomDialogTitle } from './GenericDialog'

interface IGenericDialogProps {
  title?: React.ReactNode
  titleTypography?: TypographyProps
  pop?: any
  topActions?: React.ReactNode[]
  actions?: React.ReactNode[]
  topActionsProps?: DialogActionsProps
  sx?: SxProps<Theme> | undefined
  removedCloseButton?: boolean
  titleSectionTopPadding?: string
  titleSectionBottomPadding?: string
  titleSectionXPadding?: string
  closeIconSize?: string
  fontWeight?: number
  renderCustomHeader?: (pop?: any) => React.ReactNode
  /** Full-width rule under the title (card-style forms) */
  titleDivider?: boolean
  /** Rule above footer actions */
  actionsDivider?: boolean
  /** Merged into `DialogActions` `sx` (after defaults) */
  dialogActionsSx?: SxProps<Theme>
}
export const GenericDialogLayout: React.FC<IGenericDialogProps> = ({
  title,
  titleTypography,
  children,
  pop,
  topActions,
  actions,
  topActionsProps,
  sx,
  removedCloseButton = true,
  titleSectionTopPadding,
  titleSectionBottomPadding,
  titleSectionXPadding,
  closeIconSize,
  fontWeight,
  renderCustomHeader,
  titleDivider,
  actionsDivider,
  dialogActionsSx,
}) => {
  return (
    <>
      {renderCustomHeader ? (
        renderCustomHeader(pop)
      ) : title ? (
        <CustomDialogTitle
          onClose={pop}
          isTopActionAvailable={Boolean(topActions)}
          titleSectionTopPadding={titleSectionTopPadding}
          titleTypography={titleTypography}
          titleSectionBottomPadding={titleSectionBottomPadding}
          titleSectionXPadding={titleSectionXPadding}
          closeIconSize={closeIconSize}
          fontWeight={fontWeight}
        >
          {title}
        </CustomDialogTitle>
      ) : (
        <></>
      )}
      {!renderCustomHeader && title && titleDivider && <Divider />}
      {topActions && (
        <DialogActions {...topActionsProps}>
          {topActions.map((action, i) => (
            <React.Fragment key={i}>{action}</React.Fragment>
          ))}
        </DialogActions>
      )}
      <DialogContent
        sx={{ paddingX: '28px', paddingTop: topActions ? '0px' : undefined, width: '100%', height: '100% ', ...sx }}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          paddingBottom: '16px',
          paddingTop: '0px',
          paddingRight: '36px',
          paddingLeft: '36px',
          ...(actionsDivider && {
            borderTop: 1,
            borderColor: 'divider',
            paddingTop: 1.5,
            paddingBottom: 1.5,
            paddingLeft: 2,
            paddingRight: 2,
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }),
          ...dialogActionsSx,
        }}
      >
        {!removedCloseButton && (
          <CancelButton onClick={pop} data-cy="cancel">
            {t('cancel', { ns: 'common' })}
          </CancelButton>
        )}
        {actions}
      </DialogActions>
    </>
  )
}

export const GenericDialogLayoutStyled: React.FC<IGenericDialogProps> = ({
  title,
  children,
  pop,
  topActions,
  actions,
  removedCloseButton = true,
}) => {
  return (
    <>
      {title && <CustomDialogTitleStyled isTopActionAvailable={Boolean(topActions)}>{title}</CustomDialogTitleStyled>}
      {topActions && <DialogTopActionsStyled>{topActions}</DialogTopActionsStyled>}
      <DialogContentStyled sx={{ width: '100%', height: '100% ' }}>{children}</DialogContentStyled>
      <DialogActionsStyled>
        {!removedCloseButton && <CancelButton onClick={pop}>{t('cancel', { ns: 'common' })}</CancelButton>}
        {actions}
      </DialogActionsStyled>
    </>
  )
}

const CustomDialogTitleStyled = styled(CustomDialogTitle)`
  padding: 40px;
  padding-bottom: 10px;
  & .MuiIconButton-sizeMedium {
    margin-top: 40px;
    margin-right: 20px;
  }
`

const DialogContentStyled = styled(DialogContent)`
  padding-left: 40px;
  padding-right: 40px;
`

const DialogTopActionsStyled = styled(DialogContent)`
  padding-left: 40px;
  padding-right: 40px;
`

const DialogActionsStyled = styled(DialogActions)`
  padding: 40px;
  padding-top: 10px;
`
